import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";

function normalizeId(value) {
  return value
    .trim()
    .replace(/\s+/g, "")
    .replace(/[^a-zA-Z0-9_-]/g, "");
}

function getParentStoragePath(path) {
  if (!path) return "";

  const parts = path.split("/").filter(Boolean);

  const lastPart = parts[parts.length - 1] || "";

  if (lastPart.includes(".")) {
    parts.pop();
  }

  return parts.join("/");
}

const storageUrlMemoryCache = new Map();

function getCachedStorageUrl(path) {
  if (!path) return "";

  if (storageUrlMemoryCache.has(path)) {
    return storageUrlMemoryCache.get(path);
  }

  try {
    const raw = localStorage.getItem(`storage_url_cache:${path}`);

    if (!raw) return "";

    const parsed = JSON.parse(raw);

    if (!parsed?.url) return "";

    storageUrlMemoryCache.set(path, parsed.url);

    return parsed.url;
  } catch {
    return "";
  }
}

function setCachedStorageUrl(path, url) {
  if (!path || !url) return;

  storageUrlMemoryCache.set(path, url);

  try {
    localStorage.setItem(
      `storage_url_cache:${path}`,
      JSON.stringify({
        url,
        cachedAt: Date.now(),
      })
    );
  } catch {
    // Si localStorage está lleno o bloqueado, seguimos usando cache en memoria.
  }
}

function removeCachedStorageUrl(path) {
  if (!path) return;

  storageUrlMemoryCache.delete(path);

  try {
    localStorage.removeItem(`storage_url_cache:${path}`);
  } catch {
    // Ignorar.
  }
}

function clearMinigamesIconCache() {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("storage_url_cache:Minigames/")) {
        localStorage.removeItem(key);
      }
    });

    storageUrlMemoryCache.clear();

    alert("Cache de iconos limpiada.");
  } catch {
    alert("No se pudo limpiar la cache.");
  }
}

function StorageImage({ path, alt, className, fallback = "🎮" }) {
  const [url, setUrl] = useState(() => getCachedStorageUrl(path));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadImage() {
      if (!path) {
        setUrl("");
        return;
      }

      const cachedUrl = getCachedStorageUrl(path);

      if (cachedUrl) {
        setUrl(cachedUrl);
        setFailed(false);
        return;
      }

      try {
        const imageRef = ref(storage, path);
        const downloadUrl = await getDownloadURL(imageRef);

        setCachedStorageUrl(path, downloadUrl);

        if (active) {
          setUrl(downloadUrl);
          setFailed(false);
        }
      } catch (error) {
        console.error("No se pudo cargar imagen de Storage:", path, error);

        if (active) {
          setUrl("");
          setFailed(true);
        }
      }
    }

    loadImage();

    return () => {
      active = false;
    };
  }, [path]);

  if (failed) {
    return <span className={className}>⚠️</span>;
  }

  if (!url) {
    return <span className={className}>{fallback}</span>;
  }

  return (
    <img
      className={className}
      src={url}
      alt={alt || "Icono"}
      loading="lazy"
      decoding="async"
    />
  );
}

function MinigamesPage() {
  const navigate = useNavigate();

  const [minigames, setMinigames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMinigame, setEditingMinigame] = useState(null);

  const [form, setForm] = useState({
    id: "",
    title: "",
    type: "",
    color: "#00FF00",
    iconFile: null,
    iconMiniFile: null,
  });

  const [editForm, setEditForm] = useState({
    title: "",
    type: "",
    color: "#00FF00",
    enabled: false,
    iconFile: null,
    iconMiniFile: null,
  });

  function openStoragePath(path) {
    const cleanPath = getParentStoragePath(path);

    navigate(`/admin/storage?path=${encodeURIComponent(cleanPath)}`);
  }

  async function loadMinigames() {
    try {
      setLoading(true);

      const minigamesQuery = query(
        collection(db, "minigames"),
        orderBy("title", "asc")
      );

      const snapshot = await getDocs(minigamesQuery);

      const data = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }));

      setMinigames(data);
    } catch (error) {
      console.error("Error cargando minijuegos:", error);
      alert("No se pudieron cargar los minijuegos.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMinigames();
  }, []);

  function handleChange(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function handleEditChange(field, value) {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function resetCreateForm() {
    setForm({
      id: "",
      title: "",
      type: "",
      color: "#00FF00",
      iconFile: null,
      iconMiniFile: null,
    });

    const iconInput = document.getElementById("iconFileInput");
    const iconMiniInput = document.getElementById("iconMiniFileInput");

    if (iconInput) iconInput.value = "";
    if (iconMiniInput) iconMiniInput.value = "";
  }

  function openCreateModal() {
    setShowCreateModal(true);
  }

  function closeCreateModal() {
    if (saving) return;

    setShowCreateModal(false);
    resetCreateForm();
  }

  function openEditModal(minigame) {
    setEditingMinigame(minigame);

    setEditForm({
      title: minigame.title || "",
      type: minigame.type || "",
      color: minigame.color || "#00FF00",
      enabled: minigame.enabled ?? false,
      iconFile: null,
      iconMiniFile: null,
    });
  }

  function closeEditModal() {
    if (saving) return;

    setEditingMinigame(null);

    setEditForm({
      title: "",
      type: "",
      color: "#00FF00",
      enabled: false,
      iconFile: null,
      iconMiniFile: null,
    });

    const editIconInput = document.getElementById("editIconFileInput");
    const editIconMiniInput = document.getElementById("editIconMiniFileInput");

    if (editIconInput) editIconInput.value = "";
    if (editIconMiniInput) editIconMiniInput.value = "";
  }

  async function handleCreateMinigame(e) {
    e.preventDefault();

    const minigameId = normalizeId(form.id);

    if (!minigameId) {
      alert("Ingresa un ID válido para el minijuego.");
      return;
    }

    if (!form.title.trim()) {
      alert("Ingresa el Key Título del minijuego.");
      return;
    }

    if (!form.type.trim()) {
      alert("Ingresa el type del minijuego.");
      return;
    }

    if (!form.iconFile) {
      alert("Selecciona el icon.png.");
      return;
    }

    if (!form.iconMiniFile) {
      alert("Selecciona el iconMini.png.");
      return;
    }

    try {
      setSaving(true);

      const packPath = `Minigames/${minigameId}`;
      const iconPath = `${packPath}/icon.png`;
      const iconMiniPath = `${packPath}/iconMini.png`;

      await uploadBytes(ref(storage, iconPath), form.iconFile, {
        contentType: form.iconFile.type || "image/png",
        cacheControl: "public,max-age=3600",
      });

      await uploadBytes(ref(storage, iconMiniPath), form.iconMiniFile, {
        contentType: form.iconMiniFile.type || "image/png",
        cacheControl: "public,max-age=3600",
      });

      removeCachedStorageUrl(iconPath);
      removeCachedStorageUrl(iconMiniPath);

      await setDoc(doc(db, "minigames", minigameId), {
        title: form.title.trim(),
        type: form.type.trim(),
        color: form.color,
        enabled: false,
        packPath,
        iconPath,
        iconMiniPath,
      });

      resetCreateForm();
      setShowCreateModal(false);
      await loadMinigames();

      alert("Minijuego creado correctamente.");
    } catch (error) {
      console.error("Error creando minijuego:", error);
      alert("No se pudo crear el minijuego.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateMinigame(e) {
    e.preventDefault();

    if (!editingMinigame) return;

    if (!editForm.title.trim()) {
      alert("Ingresa el Key Título del minijuego.");
      return;
    }

    if (!editForm.type.trim()) {
      alert("Ingresa el type del minijuego.");
      return;
    }

    try {
      setSaving(true);

      const minigameId = editingMinigame.id;
      const packPath = editingMinigame.packPath || `Minigames/${minigameId}`;
      const iconPath = editingMinigame.iconPath || `${packPath}/icon.png`;
      const iconMiniPath =
        editingMinigame.iconMiniPath || `${packPath}/iconMini.png`;

      if (editForm.iconFile) {
        await uploadBytes(ref(storage, iconPath), editForm.iconFile, {
          contentType: editForm.iconFile.type || "image/png",
          cacheControl: "public,max-age=3600",
        });

        removeCachedStorageUrl(iconPath);
      }

      if (editForm.iconMiniFile) {
        await uploadBytes(ref(storage, iconMiniPath), editForm.iconMiniFile, {
          contentType: editForm.iconMiniFile.type || "image/png",
          cacheControl: "public,max-age=3600",
        });

        removeCachedStorageUrl(iconMiniPath);
      }

      const updatedData = {
        title: editForm.title.trim(),
        type: editForm.type.trim(),
        color: editForm.color,
        enabled: editForm.enabled,
        packPath,
        iconPath,
        iconMiniPath,
      };

      await updateDoc(doc(db, "minigames", minigameId), updatedData);

      setMinigames((prevMinigames) =>
        prevMinigames.map((minigame) =>
          minigame.id === minigameId
            ? {
                ...minigame,
                ...updatedData,
              }
            : minigame
        )
      );

      closeEditModal();

      alert("Minijuego actualizado correctamente.");
    } catch (error) {
      console.error("Error actualizando minijuego:", error);
      alert("No se pudo actualizar el minijuego.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleMinigameEnabled(minigame) {
    try {
      setSaving(true);

      const nextEnabled = !minigame.enabled;

      await updateDoc(doc(db, "minigames", minigame.id), {
        enabled: nextEnabled,
      });

      setMinigames((prevMinigames) =>
        prevMinigames.map((item) =>
          item.id === minigame.id
            ? {
                ...item,
                enabled: nextEnabled,
              }
            : item
        )
      );
    } catch (error) {
      console.error("Error cambiando estado del minijuego:", error);
      alert("No se pudo cambiar el estado del minijuego.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="admin-section-card minigames-page">
        <div className="section-header minigames-header">
          <div>
            <span className="section-eyebrow">Contenido</span>
            <h2>Minijuegos</h2>
            <p>
              Crea, edita y controla el estado de los minijuegos. Los íconos se
              guardan en Firebase Storage.
            </p>
          </div>

          <div className="section-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={loadMinigames}
              disabled={saving}
            >
              🔄 Actualizar
            </button>

            <button
              type="button"
              className="secondary-button"
              onClick={clearMinigamesIconCache}
              disabled={saving}
            >
              🧹 Limpiar cache
            </button>

            <button
              type="button"
              className="primary-action-button"
              onClick={openCreateModal}
              disabled={saving}
            >
              ＋ Crear minijuego
            </button>

            <span className="section-count">{minigames.length} minijuegos</span>
          </div>
        </div>

        <div className="minigames-summary-grid">
          <article className="minigame-summary-card">
            <span>🎮</span>
            <div>
              <strong>{minigames.length}</strong>
              <small>Total</small>
            </div>
          </article>

          <article className="minigame-summary-card">
            <span>✅</span>
            <div>
              <strong>
                {minigames.filter((minigame) => minigame.enabled).length}
              </strong>
              <small>Activos</small>
            </div>
          </article>

          <article className="minigame-summary-card">
            <span>⏸️</span>
            <div>
              <strong>
                {minigames.filter((minigame) => !minigame.enabled).length}
              </strong>
              <small>Inactivos</small>
            </div>
          </article>
        </div>

        <div className="minigames-list">
          <div className="minigames-list__header">
            <div>
              <h3>Minijuegos existentes</h3>
              <p>Administra los minijuegos creados en Firestore.</p>
            </div>
          </div>

          {loading ? (
          <main className="users-loading-state">
            <section className="loading-card">
              <div className="banter-loader" aria-hidden="true">
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
              </div>

              <h2>Cargando dashboard...</h2>
              <p>Consultando usuarios, minijuegos, tienda, misiones y temporada.</p>
            </section>
          </main>
          ) : minigames.length === 0 ? (
            <p className="empty-state">No hay minijuegos creados.</p>
          ) : (
            <div className="table-wrapper">
              <table className="admin-table minigames-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Key Título</th>
                    <th>Type</th>
                    <th>Iconos</th>
                    <th>Estado</th>
                    <th>Pack Path</th>
                    <th>Acciones</th>
                  </tr>
                </thead>

                <tbody>
                  {minigames.map((minigame) => (
                    <tr key={minigame.id}>
                      <td>
                        <code className="table-code">{minigame.id}</code>
                      </td>

                      <td>
                        <div className="minigame-title-cell">
                          <button
                            type="button"
                            className="minigame-color-avatar minigame-color-avatar--image minigame-icon-button"
                            style={{
                              background: minigame.color || "#2563eb",
                            }}
                            onClick={() => openStoragePath(minigame.iconMiniPath)}
                            title="Abrir iconMini en Storage"
                          >
                            <StorageImage
                              path={minigame.iconMiniPath}
                              alt={`${minigame.title || minigame.id} icon mini`}
                              className="minigame-mini-image"
                            />
                          </button>

                          <div className="minigame-title-text">
                            <strong>{minigame.title || "—"}</strong>
                            <span>{minigame.color || "Sin color"}</span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="type-pill">
                          {minigame.type || "—"}
                        </span>
                      </td>

                      <td>
                        <div className="minigame-icons-cell">
                          <button
                            type="button"
                            className="minigame-icon-preview minigame-icon-preview--button"
                            onClick={() => openStoragePath(minigame.iconPath)}
                            title="Abrir icon.png en Storage"
                          >
                            <StorageImage
                              path={minigame.iconPath}
                              alt={`${minigame.title || minigame.id} icon`}
                              className="minigame-storage-image"
                            />
                            <span>icon</span>
                          </button>

                          <button
                            type="button"
                            className="minigame-icon-preview minigame-icon-preview--button"
                            onClick={() =>
                              openStoragePath(minigame.iconMiniPath)
                            }
                            title="Abrir iconMini.png en Storage"
                          >
                            <StorageImage
                              path={minigame.iconMiniPath}
                              alt={`${minigame.title || minigame.id} icon mini`}
                              className="minigame-storage-image"
                            />
                            <span>mini</span>
                          </button>
                        </div>
                      </td>

                      <td>
                        <button
                          type="button"
                          className={
                            minigame.enabled
                              ? "status-badge status-badge--enabled status-badge--button"
                              : "status-badge status-badge--disabled status-badge--button"
                          }
                          onClick={() => toggleMinigameEnabled(minigame)}
                          disabled={saving}
                        >
                          {minigame.enabled ? "Activo" : "Inactivo"}
                        </button>
                      </td>

                      <td>
                        <button
                          type="button"
                          className="path-code path-code--button"
                          onClick={() => openStoragePath(minigame.packPath)}
                          title="Abrir carpeta en Storage"
                        >
                          {minigame.packPath || "—"}
                        </button>
                      </td>

                      <td>
                        <button
                          className="small-button small-button--dark"
                          onClick={() => openEditModal(minigame)}
                          disabled={saving}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {showCreateModal && (
        <div className="modal-backdrop">
          <div className="edit-modal edit-modal--wide minigame-create-modal">
            <div className="edit-modal__header">
              <div>
                <span className="section-eyebrow">Nuevo minijuego</span>
                <h3>Crear minijuego</h3>
                <p>Completa la información y sube los dos íconos requeridos.</p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeCreateModal}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form
              className="minigame-form minigame-form--modal"
              onSubmit={handleCreateMinigame}
            >
              <div className="form-grid">
                <label>
                  ID del documento
                  <input
                    type="text"
                    placeholder="Frogger"
                    value={form.id}
                    onChange={(e) => handleChange("id", e.target.value)}
                    disabled={saving}
                  />
                  <small>
                    Se usará para crear Minigames/ID/icon.png y el documento
                    minigames/ID.
                  </small>
                </label>

                <label>
                  Key Título
                  <input
                    type="text"
                    placeholder="Rana"
                    value={form.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    disabled={saving}
                  />
                </label>

                <label>
                  Type
                  <input
                    type="text"
                    placeholder="frog"
                    value={form.type}
                    onChange={(e) => handleChange("type", e.target.value)}
                    disabled={saving}
                  />
                </label>

                <label>
                  Color
                  <div className="color-input-row">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => handleChange("color", e.target.value)}
                      disabled={saving}
                    />

                    <input
                      type="text"
                      value={form.color}
                      onChange={(e) => handleChange("color", e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </label>

                <label>
                  Icon
                  <input
                    id="iconFileInput"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) =>
                      handleChange("iconFile", e.target.files?.[0] || null)
                    }
                    disabled={saving}
                  />
                  <small>Se guardará como icon.png</small>
                </label>

                <label>
                  Icon Mini
                  <input
                    id="iconMiniFileInput"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) =>
                      handleChange("iconMiniFile", e.target.files?.[0] || null)
                    }
                    disabled={saving}
                  />
                  <small>Se guardará como iconMini.png</small>
                </label>
              </div>

              <div className="path-preview">
                <strong>Vista previa de paths</strong>
                <span>packPath: Minigames/{normalizeId(form.id) || "ID"}</span>
                <span>
                  iconPath: Minigames/{normalizeId(form.id) || "ID"}/icon.png
                </span>
                <span>
                  iconMiniPath: Minigames/{normalizeId(form.id) || "ID"}
                  /iconMini.png
                </span>
                <span>enabled: false</span>
              </div>

              <div className="edit-modal__actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeCreateModal}
                  disabled={saving}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="primary-action-button"
                  disabled={saving}
                >
                  {saving ? "Creando..." : "Crear minijuego"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingMinigame && (
        <div className="modal-backdrop">
          <div className="edit-modal edit-modal--wide">
            <div className="edit-modal__header">
              <div>
                <span className="section-eyebrow">Editar</span>
                <h3>Editar minijuego</h3>
                <p>{editingMinigame.id}</p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeEditModal}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateMinigame} className="edit-form">
              <div className="form-grid">
                <label>
                  Key Título
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => handleEditChange("title", e.target.value)}
                    disabled={saving}
                  />
                </label>

                <label>
                  Type
                  <input
                    type="text"
                    value={editForm.type}
                    onChange={(e) => handleEditChange("type", e.target.value)}
                    disabled={saving}
                  />
                </label>

                <label>
                  Color
                  <div className="color-input-row">
                    <input
                      type="color"
                      value={editForm.color}
                      onChange={(e) =>
                        handleEditChange("color", e.target.value)
                      }
                      disabled={saving}
                    />

                    <input
                      type="text"
                      value={editForm.color}
                      onChange={(e) =>
                        handleEditChange("color", e.target.value)
                      }
                      disabled={saving}
                    />
                  </div>
                </label>

                <label className="edit-switch-label">
                  Estado del minijuego
                  <div className="switch-row">
                    <input
                      type="checkbox"
                      checked={editForm.enabled}
                      onChange={(e) =>
                        handleEditChange("enabled", e.target.checked)
                      }
                      disabled={saving}
                    />
                    <span className="switch-ui"></span>
                    <strong>{editForm.enabled ? "Activo" : "Inactivo"}</strong>
                  </div>
                </label>
              </div>

              <div className="edit-file-grid">
                <label>
                  Reemplazar Icon
                  <input
                    id="editIconFileInput"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) =>
                      handleEditChange("iconFile", e.target.files?.[0] || null)
                    }
                    disabled={saving}
                  />
                  <small>
                    Se reemplazará en:{" "}
                    {editingMinigame.iconPath ||
                      `Minigames/${editingMinigame.id}/icon.png`}
                  </small>
                </label>

                <label>
                  Reemplazar Icon Mini
                  <input
                    id="editIconMiniFileInput"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) =>
                      handleEditChange(
                        "iconMiniFile",
                        e.target.files?.[0] || null
                      )
                    }
                    disabled={saving}
                  />
                  <small>
                    Se reemplazará en:{" "}
                    {editingMinigame.iconMiniPath ||
                      `Minigames/${editingMinigame.id}/iconMini.png`}
                  </small>
                </label>
              </div>

              <div className="path-preview">
                <strong>Este formulario no cambia el ID ni los paths</strong>
                <span>
                  packPath:{" "}
                  {editingMinigame.packPath ||
                    `Minigames/${editingMinigame.id}`}
                </span>
                <span>
                  iconPath:{" "}
                  {editingMinigame.iconPath ||
                    `Minigames/${editingMinigame.id}/icon.png`}
                </span>
                <span>
                  iconMiniPath:{" "}
                  {editingMinigame.iconMiniPath ||
                    `Minigames/${editingMinigame.id}/iconMini.png`}
                </span>
              </div>

              <div className="edit-modal__actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeEditModal}
                  disabled={saving}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="primary-action-button"
                  disabled={saving}
                >
                  {saving ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default MinigamesPage;